import Layout from "./views/Layouts/Index";
import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { ThemeProvider } from "styled-components";
// import Dashboard from "./views/Pages/Company/Dashboard";
import InitialPage from "./views/Pages/Common/PanelDashboard";
import Login from "./views/Pages/Auth/index";
import SignUp from "./views/Pages/Auth/SignUp";
import Token from "./views/Pages/Auth/Token";
import EmployeeSingUp from "./views/Pages/Auth/EmployeeSingUp";
import CompanySelect from "./views/Pages/Auth/CompanySelect";
import AccountNotRegistered from "./views/Pages/Auth/AccountNotRegistered";
import { NoMatch, Unauthorized } from "./views/Pages/Common/StatusPages";
import NewCompany from "./views/Pages/Company/Companies/NewCompany";
import Employees from "./views/Pages/Company/Employees";
import NFes from "./views/Pages/Company/NFes";
import Documents from "./views/Pages/Company/Documents";
import Alerts from "./views/Pages/Company/Alerts";
import NFesEmployee from "./views/Pages/Employee/NFes";

// CSS do react-toastify como base; o visual vem do ToastContainer da lib.
import 'react-toastify/dist/ReactToastify.css';
// CSS do calendário (react-datepicker) do DatePicker da lib.
import "@ficatranquilo/contabilidade.com-ui/style.css";
import { MobileAccessProvider, ToastContainer } from "@ficatranquilo/contabilidade.com-ui";
import { LIB_THEME, LibGlobalStyle, OW_ASSETS } from "@components/ow";
import Declarations from "./views/Pages/Company/Declarations";
import DocumentsEmployee from './views/Pages/Employee/Documents'
import DeclarationsEmployee from "./views/Pages/Employee/Declarations";
import Details from "./views/Pages/Company/Employees/Details";
import Quiz from "./views/Pages/Company/Quiz";
import NewQuiz from "./views/Pages/Company/Quiz/NewQuiz";

import Auth from './providers/auth/Auth';
import EditQuiz from "./views/Pages/Company/Quiz/EditQuiz";
import QuizEmployee from "./views/Pages/Employee/Quiz";
import AnswerQuiz from "./views/Pages/Employee/Quiz/AnswerQuiz";
import QuizAnswers from "./views/Pages/Company/Quiz/QuizAnswers";
import Profile from "./views/Pages/Common/Profile";
import Config from "./views/Pages/Company/Config";
import ApiKeys from "./views/Pages/Company/ApiKeys";
import BankEmployee from "./views/Pages/Employee/Bank";
import Projects from "./views/Pages/Company/Projects";
import NewProject from "./views/Pages/Company/Projects/NewProject";
import ProjectDetails from "./views/Pages/Company/Projects/Details";
import ProjectsEmployee from "./views/Pages/Employee/Projects";
import ProjectDetailsEmployee from "./views/Pages/Employee/Projects/Details";
import Contracts from "./views/Pages/Company/Contracts";
import NewContract from "./views/Pages/Company/Contracts/NewContract";
import EditContract from "./views/Pages/Company/Contracts/EditContract";
import ContractPatterns from "./views/Pages/Company/ContractPatterns";
import NewContractPattern from "./views/Pages/Company/ContractPatterns/NewContractPattern";
import EditContractPattern from "./views/Pages/Company/ContractPatterns/EditContractPattern";
import ContractDetails from "./views/Pages/Company/Contracts/Details";
import ApproveValues from "./views/Pages/Company/Contracts/ApproveValues";
import PaymentSchedule from "./views/Pages/Company/PaymentSchedule";
import PaymentScheduleDetails from "./views/Pages/Company/PaymentSchedule/Details";
import PaymentScheduleEmployee from "./views/Pages/Employee/PaymentSchedule";
import PaymentScheduleDetailsEmployee from "./views/Pages/Employee/PaymentSchedule/Details";
import ContractsEmployee from "./views/Pages/Employee/Contracts";
import ContractDetailsEmployee from "./views/Pages/Employee/Contracts/Details";
import HoursApproval from "./views/Pages/Company/HoursApproval";
import Permissions from "./views/Pages/Company/Permissions";
import UserPermissions from "./views/Pages/Company/Permissions/UserPermissions";
import ConfidentialGroups from "./views/Pages/Company/ConfidentialGroups";
import ConfidentialGroupDetails from "./views/Pages/Company/ConfidentialGroups/Details";
import TimeClockCompany from "./views/Pages/Company/TimeClock";
import TimeClockAdjustments from "./views/Pages/Company/TimeClock/Adjustments";
import TimeClockEmployee from "./views/Pages/Employee/TimeClock";

// Vitrine do design system (/dev/ow): só em desenvolvimento; no build de produção
// o ramo some e o chunk nem é gerado.
const OwShowcase =
	process.env.NODE_ENV === "development" ? lazy(() => import("./views/Pages/Dev/OwShowcase")) : null;

/** /quiz/view/:id (a tela antiga, fora do AppShell) → respostas no ADM, responder no EMP. */
function QuizViewRedirect() {
	const { id } = useParams();
	const user = JSON.parse(localStorage.getItem("user_ow") || "{}");
	return <Navigate to={user.role === "ADM" ? `/c/quiz/${id}/answers` : `/e/quiz/${id}`} replace />;
}

/** /c/contracts/:id/value-input (sem link desde mar/2026) → aba Valores variáveis do contrato. */
function ValueInputRedirect() {
	const { id } = useParams();
	return <Navigate to={`/c/contracts/${id}?tab=valores`} replace />;
}

// Tema da lib @ficatranquilo/contabilidade.com-ui, com a ação do OneWork, para o app
// inteiro (Layout, login, convite, quiz, toasts). A cor da empresa chega pelas vars
// --ow-* que o applyTenantTheme() grava, sem recriar o tema.
function App() {
	return (
		<ThemeProvider theme={LIB_THEME}>
			<LibGlobalStyle />
			<AppRoutes />
		</ThemeProvider>
	);
}

// Abaixo de 1200px, tela de aviso no lugar das rotas, como no ft-app-web. O OneWork não
// tem aplicativo: sem lojas e sem imagem.
const MOBILE_ACCESS = {
	$logo: <img src={OW_ASSETS.full} alt="OneWork" style={{ height: 40, width: "auto" }} />,
	$title: "<strong>O OneWork</strong> funciona no computador",
	$description: "Para usar a plataforma, acesse por um computador com a tela maior.",
};

function AppRoutes() {

	return (
		<div id="App">
			{/* Canto superior direito: o Header da lib não é fixo, então não há topbar a evitar */}
			<ToastContainer position="top-right" />
			<MobileAccessProvider {...MOBILE_ACCESS}>
			<Routes>
				<Route path="/auth/token" element={<Token />} />
				<Route path="/auth/entrar" element={<Login />} />
				<Route path="sign-up" element={<SignUp />} />
				<Route path="/register/e/:token" element={<EmployeeSingUp />} />
				<Route path="/auth/select-company" element={<CompanySelect />} />
				<Route path="/auth/conta-nao-cadastrada" element={<AccountNotRegistered />} />
				<Route path="/" element={<Layout />}>
					<Route index element={<InitialPage />} />
					{/* Link antigo dos questionários: dentro do Layout, para valer o login */}
					<Route path="/quiz/view/:id" element={<QuizViewRedirect />} />

					{/* Company Routes */}
					<Route element={<Auth allowedRoles={["ADM"]} />}>
						<Route path="/config" element={<Config />} />
						<Route path="/c/companies/new" element={<NewCompany />} />
												<Route path="/c/projects/new" element={<NewProject />} />
												<Route path="/c/contract-patterns" element={<ContractPatterns />} />
						<Route path="/c/contract-patterns/new" element={<NewContractPattern />} />
						<Route path="/c/contract-patterns/:id/edit" element={<EditContractPattern />} />
						<Route path="/c/contracts/new" element={<NewContract />} />
						<Route path="/c/contracts/:id/edit" element={<EditContract />} />
						{/* Tela antiga de informar valor: agora é o "Informar valor" da aba Valores variáveis */}
						<Route path="/c/contracts/:id/value-input" element={<ValueInputRedirect />} />
						{/* Reclamações saíram do back em mai/2025: o tipo "Reclamação" fica nas declarações */}
						<Route path="/c/claims" element={<Navigate to="/c/declarations" replace />} />
						<Route path="/c/declarations" element={<Declarations />} />
						<Route path="/c/alerts" element={<Alerts />} />
						<Route path="/c/quiz" element={<Quiz />} />
						<Route path="/c/quiz/new" element={<NewQuiz />} />
						<Route path="/c/quiz/:id/answers" element={<QuizAnswers />} />
						<Route path="/c/quiz/:id" element={<EditQuiz />} />
						<Route path="/c/api-keys" element={<ApiKeys />} />
						{/* Tela antiga de aprovadores: agora é um filtro de /c/permissions */}
						<Route path="/c/approvers" element={<Navigate to="/c/permissions?grupo=project_hours" replace />} />
						<Route path="/c/permissions" element={<Permissions />} />
						<Route path="/c/permissions/:userId" element={<UserPermissions />} />
						<Route path="/c/confidential-groups" element={<ConfidentialGroups />} />
						<Route path="/c/confidential-groups/:id" element={<ConfidentialGroupDetails />} />
					</Route>

					{/* Aprovação de horas: ADM ou quem vê horas (só leitura) ou aprova */}
					<Route element={<Auth allowedRoles={["ADM"]} screen="hoursApproval" />}>
						<Route path="/c/hours-approval" element={<HoursApproval />} />
					</Route>

					{/* Projetos: ADM, "Ver projetos" (todos) ou itens de horas (os projetos em que valem) */}
					<Route element={<Auth allowedRoles={["ADM"]} screen="projects" />}>
						<Route path="/c/projects" element={<Projects />} />
						<Route path="/c/projects/:id" element={<ProjectDetails />} />
					</Route>

					{/* Contratos: ADM ou "Ver contratos" (só leitura; criar, editar e valores seguem só ADM) */}
					<Route element={<Auth allowedRoles={["ADM"]} screen="contracts" />}>
						<Route path="/c/contracts" element={<Contracts />} />
						<Route path="/c/contracts/:id" element={<ContractDetails />} />
					</Route>

					{/* Cronogramas e lançamentos de valor: ADM ou "Ver cronogramas" (só leitura) */}
					<Route element={<Auth allowedRoles={["ADM"]} screen="paymentSchedules" />}>
						<Route path="/c/payment-schedules" element={<PaymentSchedule />} />
						<Route path="/c/payment-schedules/:id" element={<PaymentScheduleDetails />} />
						<Route path="/c/contracts/value-approvals" element={<ApproveValues />} />
					</Route>

					{/* Colaboradores: ADM ou "Ver colaboradores" (só leitura; convidar, importar, editar e excluir seguem só ADM) */}
					<Route element={<Auth allowedRoles={["ADM"]} screen="employees" />}>
						<Route path="/c/employees" element={<Employees />} />
						<Route path="/c/employe/:id" element={<Details />} />
					</Route>

					{/* Notas fiscais: ADM ou "Ver NFs" (ver e baixar; aprovar e pagar seguem só ADM) */}
					<Route element={<Auth allowedRoles={["ADM"]} screen="taxInvoices" />}>
						<Route path="/c/nfes" element={<NFes />} />
					</Route>

					{/* Documentos: ADM ou "Ver documentos" (ver e baixar; enviar, editar e excluir seguem só ADM) */}
					<Route element={<Auth allowedRoles={["ADM"]} screen="documents" />}>
						<Route path="/c/documents" element={<Documents />} />
					</Route>

					{/* Ponto CLT lado empresa: ADM ou gestores de ponto (time_clock:read) */}
					<Route element={<Auth allowedRoles={["ADM"]} screen="timeClock" />}>
						<Route path="/c/time-clock" element={<TimeClockCompany />} />
						<Route path="/c/time-clock/adjustments" element={<TimeClockAdjustments />} />
					</Route>


					{/* Employee Routes */}
					<Route element={<Auth allowedRoles={["EMP"]} />}>
						<Route path="/e/nfes" element={<NFesEmployee />} />					<Route path="/e/payment-schedules" element={<PaymentScheduleEmployee />} />
						<Route path="/e/payment-schedules/:id" element={<PaymentScheduleDetailsEmployee />} />
												<Route path="/e/projects" element={<ProjectsEmployee />} />
						<Route path="/e/projects/:id" element={<ProjectDetailsEmployee />} />
						<Route path="/e/contracts" element={<ContractsEmployee />} />
						<Route path="/e/contracts/:id" element={<ContractDetailsEmployee />} />
						<Route path="/e/claims" element={<Navigate to="/e/declarations" replace />} />
						<Route path="/e/declarations" element={<DeclarationsEmployee />} />
						<Route path="/e/documents" element={<DocumentsEmployee />} />
						<Route path="/e/quiz" element={<QuizEmployee />} />
						<Route path="/e/quiz/:id" element={<AnswerQuiz />} />
						<Route path="/e/bank" element={<BankEmployee />} />
						<Route path="/e/time-clock" element={<TimeClockEmployee />} />
					</Route>

					{OwShowcase && (
						<Route
							path="/dev/ow"
							element={
								<Suspense fallback={null}>
									<OwShowcase />
								</Suspense>
							}
						/>
					)}
					<Route path="/profile" element={<Profile />} />
					<Route path="/unauthorized" element={<Unauthorized />} />
					<Route path="*" element={<NoMatch />} />
				</Route>

			</Routes>
			</MobileAccessProvider>
		</div>
	);
}

export default App;
